import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  History, 
  Filter, 
  User, 
  Calendar, 
  ChevronLeft, 
  ChevronRight,
  Lock,
  Mail,
  Phone,
  Camera,
  Tag,
  UserCog,
  FileText,
  Search,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DatePicker } from '../components/DatePicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivityLog = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  
  // Filters
  const [actionTypes, setActionTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    action_type: '',
    target_user_id: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch action types and users for filter dropdowns
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [typesRes, usersRes] = await Promise.all([
          axios.get(`${API}/audit-logs/action-types`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/audit-logs/users`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setActionTypes(typesRes.data.action_types || []);
        setUsers(usersRes.data.users || []);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    if (token) fetchFilterOptions();
  }, [token]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString()
      });
      
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.target_user_id) params.append('target_user_id', filters.target_user_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, limit, filters]);

  useEffect(() => {
    if (token) fetchLogs();
  }, [fetchLogs, token]);

  const getActionIcon = (action) => {
    switch (action) {
      case 'password_change':
        return <Lock size={16} className="text-amber-500" />;
      case 'email_change_requested':
      case 'email_change_completed':
        return <Mail size={16} className="text-blue-500" />;
      case 'mobile_update':
        return <Phone size={16} className="text-green-500" />;
      case 'profile_picture_upload':
      case 'profile_picture_remove':
        return <Camera size={16} className="text-purple-500" />;
      case 'profile_update':
        return <User size={16} className="text-cyan-500" />;
      case 'brand_assignment_update':
      case 'brand_assignment_self_update':
        return <Tag size={16} className="text-orange-500" />;
      case 'role_update':
        return <UserCog size={16} className="text-red-500" />;
      default:
        return <FileText size={16} className="text-slate-500" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'password_change': 'Changed Password',
      'email_change_requested': 'Requested Email Change',
      'email_change_completed': 'Completed Email Change',
      'mobile_update': 'Updated Mobile Number',
      'profile_picture_upload': 'Uploaded Profile Picture',
      'profile_picture_remove': 'Removed Profile Picture',
      'profile_update': 'Updated Profile',
      'brand_assignment_update': 'Updated Brand Assignments',
      'brand_assignment_self_update': 'Updated Own Brands',
      'role_update': 'Updated Role',
      'user_created': 'Created User',
      'user_deleted': 'Deleted User',
      'task_created': 'Created Task',
      'task_updated': 'Updated Task',
      'task_deleted': 'Deleted Task'
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch {
      return timestamp;
    }
  };

  const renderDetails = (log) => {
    const details = log.details || {};
    const items = [];
    
    if (details.old_email && details.new_email) {
      items.push(<span key="email" className="text-xs text-slate-500">{details.old_email} → {details.new_email}</span>);
    }
    if (details.old_value !== undefined && details.new_value !== undefined) {
      items.push(<span key="value" className="text-xs text-slate-500">{details.old_value || '(empty)'} → {details.new_value || '(empty)'}</span>);
    }
    if (details.old_brands && details.new_brands) {
      const oldBrands = details.old_brands.length ? details.old_brands.join(', ') : '(none)';
      const newBrands = details.new_brands.length ? details.new_brands.join(', ') : '(none)';
      items.push(<span key="brands" className="text-xs text-slate-500">{oldBrands} → {newBrands}</span>);
    }
    if (details.target_user_email) {
      items.push(<span key="target" className="text-xs text-slate-500">Target: {details.target_user_email}</span>);
    }
    if (details.password_strength) {
      items.push(<span key="strength" className="text-xs text-slate-500">Strength: {details.password_strength}</span>);
    }
    if (details.changes) {
      Object.entries(details.changes).forEach(([key, change]) => {
        if (change.old !== change.new) {
          items.push(
            <span key={key} className="text-xs text-slate-500">
              {key.replace(/_/g, ' ')}: {change.old || '(empty)'} → {change.new || '(empty)'}
            </span>
          );
        }
      });
    }
    
    return items.length > 0 ? (
      <div className="flex flex-col gap-0.5 mt-1">{items}</div>
    ) : null;
  };

  const clearFilters = () => {
    setFilters({ action_type: '', target_user_id: '', start_date: '', end_date: '' });
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);
  const hasActiveFilters = filters.action_type || filters.target_user_id || filters.start_date || filters.end_date;

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title flex items-center gap-3">
          <History className="text-cyan-500" size={32} />
          Activity Log
        </h1>
        <p className="page-subtitle">View all system activity and changes</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
            data-testid="toggle-filters-button"
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full">
                Active
              </span>
            )}
          </Button>
          
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
              <X size={14} className="mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
        
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-200">
            <div className="space-y-2">
              <Label>Action Type</Label>
              <Select
                value={filters.action_type || "all"}
                onValueChange={(v) => { setFilters(f => ({ ...f, action_type: v === "all" ? "" : v })); setPage(0); }}
              >
                <SelectTrigger data-testid="filter-action-type">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {actionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>User</Label>
              <Select
                value={filters.target_user_id || "all"}
                onValueChange={(v) => { setFilters(f => ({ ...f, target_user_id: v === "all" ? "" : v })); setPage(0); }}
              >
                <SelectTrigger data-testid="filter-user">
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Start Date</Label>
              <DatePicker
                value={filters.start_date}
                onChange={(v) => { setFilters(f => ({ ...f, start_date: v })); setPage(0); }}
                testId="filter-start-date"
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <DatePicker
                value={filters.end_date}
                onChange={(v) => { setFilters(f => ({ ...f, end_date: v })); setPage(0); }}
                testId="filter-end-date"
              />
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="text-slate-500 mt-2">Loading activity logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <History size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No activity logs found</p>
            {hasActiveFilters && (
              <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Action</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">User</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Details</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50" data-testid={`log-row-${log.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          <span className="text-sm font-medium text-slate-800">
                            {getActionLabel(log.action)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full btn-cta flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {log.user_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{log.user_name}</p>
                            <p className="text-xs text-slate-500">{log.user_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {renderDetails(log)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Calendar size={14} />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <p className="text-sm text-slate-600">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total} entries
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="prev-page-button"
                >
                  <ChevronLeft size={16} />
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page + 1} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  data-testid="next-page-button"
                >
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
